/**
 * Configuración central del brand — lado frontend (Vite).
 *
 * Todos los valores se leen desde variables de entorno VITE_BRAND_*.
 * Los defaults corresponden al brand "Ana Fernández" / "anaafernandez".
 *
 * Para adaptar este proyecto a otro brand (multi-creator SaaS):
 *   1. Cambiar las VITE_BRAND_* en Railway (producción) o en .env.local (local).
 *   2. NO es necesario tocar ningún componente React.
 *
 * Variables gemelas dentro de ESTE proyecto (anaafernandez-main):
 *   - Frontend: src/lib/brandConfig.ts  (VITE_BRAND_*)
 *   - Backend:  api/lib/brandConfig.ts  (BRAND_*)
 *   - Worker:   cloudflare-worker-anaafernandez/ (su propio config)
 *
 * Cada brand/sitio es un proyecto separado con su propio worker — no
 * compartimos worker ni env vars entre brands.
 */

// ── Identidad ────────────────────────────────────────────────────────────────

/**
 * Nombre legal / real de la persona creadora (ej. "Ana Fernández").
 * Usado en StructuredData JSON-LD, i18n, SEO, alt text.
 */
export const BRAND_PERSON_NAME =
  import.meta.env.VITE_BRAND_PERSON_NAME ?? "Ana Fernández";

/**
 * Nombre público del brand / handle (ej. "Ana Fernández").
 * Puede ser diferente al nombre legal. Usado en footers, títulos, emails.
 */
export const BRAND_NAME =
  import.meta.env.VITE_BRAND_NAME ?? "Ana Fernández";

/**
 * Clave corta en minúsculas, sin espacios (ej. "anaafernandez").
 * Usada en:
 *   - Stripe/MP metadata `source` → identifica pagos del brand
 *   - `client_reference_id` en URLs de pago
 *   - Mux `creator_id` para filtrar videos
 *   - PostHog `site` property (project **422815** Ana Fernandez — locked)
 *   - DB: manual_transactions.source, site, brand
 * DEBE coincidir con BRAND_SOURCE_KEY en el backend fan (si aplica).
 *
 * PostHog: entire repo uses project 422815 only — see `posthogBrandAlignment`.
 */
/**
 * This monorepo deploy is **anaafernandez** only.
 * Stripe metadata.source / PostHog site / DB brand_source must match.
 * Dedicated webhook: Railway service `anaafernandez-webhook`.
 */
export const BRAND_SOURCE_KEY = (
  import.meta.env.VITE_BRAND_SOURCE_KEY ?? "anaafernandez"
)
  .toString()
  .trim() || "anaafernandez";

/** @deprecated import from posthogBrandAlignment — kept as discoverability alias */
export { BRAND_POSTHOG_PROJECT_ID } from "./posthogBrandAlignment";

/**
 * Handle en redes sociales (sin @). Ej. "anaafernandez".
 * Usado en StructuredData alternateName, SEO keywords.
 */
export const BRAND_HANDLE =
  import.meta.env.VITE_BRAND_HANDLE ?? BRAND_SOURCE_KEY;

// ── URLs ─────────────────────────────────────────────────────────────────────

/** URL del sitio principal (este proyecto). Ej. "https://anaafernandez.com" (sin www) */
export const BRAND_SITE_URL =
  import.meta.env.VITE_BRAND_SITE_URL ??
  import.meta.env.VITE_MAIN_SITE_URL ??
  `https://${BRAND_SOURCE_KEY}.com`;

/**
 * Hostname corto del sitio principal, sin protocolo ni www.
 * Ej. "anaafernandez.com". Usado en footer links, siteGeoPolicy.
 */
export const BRAND_SITE_HOST = BRAND_SITE_URL.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "");

/** URL del fan site. Ej. "https://fan site" */
export const BRAND_FAN_URL =
  import.meta.env.VITE_BRAND_FAN_URL ??
  import.meta.env.VITE_API_URL ??
  `https://fan.${BRAND_SOURCE_KEY}.com`;

// ── Pagos ────────────────────────────────────────────────────────────────────

/**
 * `client_reference_id` que se añade a URLs de Stripe/MercadoPago.
 * Igual que BRAND_SOURCE_KEY — centralizado aquí para claridad.
 */
export const BRAND_CLIENT_REFERENCE_ID = BRAND_SOURCE_KEY;

// ── Mux ──────────────────────────────────────────────────────────────────────

/**
 * `creator_id` usado como filtro en la API de Mux Data.
 * Identifica los assets de este brand en una cuenta Mux compartida.
 */
export const BRAND_MUX_CREATOR_ID =
  import.meta.env.VITE_BRAND_MUX_CREATOR_ID ?? BRAND_SOURCE_KEY;

/** `meta.external_id` del asset teaser VIP (Mux API `/api/mux-videos`). */
export const BRAND_MUX_EXTERNAL_ID_FEED = "feed";

/**
 * `meta.title` del video teaser fullscreen (InvitacionExclusivaMux, DualCTA).
 * Par con `creator_id` + `external_id=feed` en Mux Dashboard.
 */
export const BRAND_MUX_TEASER_TITLE =
  (import.meta.env.VITE_BRAND_MUX_TEASER_TITLE as string | undefined)?.trim() ||
  `${BRAND_SOURCE_KEY}-teaser`;

/**
 * Playback ID Mux garantizado del brand — última red de seguridad para
 * plantillas Mux-first (`MuxVideoPlayerWithCTA`, `DualCTAVideoLanding`,
 * `InvitacionExclusivaMux`) cuando NO hay override admin por país, config
 * runtime, metadata de Mux ni env de idioma (p. ej. geo `UNKNOWN` del Worker).
 *
 * **Por qué existe:** sin esto el fallback final era un playback ID hardcodeado
 * y compartido — en un fork multi-brand serviría el video de OTRO creador. Cada
 * brand define `VITE_BRAND_MUX_DEFAULT_PLAYBACK_ID` con SU asset. El default
 * corresponde al asset de `anaafernandez` (comportamiento previo intacto).
 *
 * Un playback ID válido en Mux es `[A-Za-z0-9_-]{8,128}` — se descarta cualquier
 * placeholder (`your-…`, `tu-…`, `xxx`) para no romper el player en producción.
 */
const BRAND_MUX_DEFAULT_PLAYBACK_ID_HARDCODED =
  "PhCWZCQmP6hVfAuCokc00jipal401oe2M38PVvBW9NSas";

function resolveBrandMuxDefaultPlaybackId(): string {
  const raw = (
    import.meta.env.VITE_BRAND_MUX_DEFAULT_PLAYBACK_ID as string | undefined
  )?.trim();
  if (
    raw &&
    /^[A-Za-z0-9_-]{8,128}$/.test(raw) &&
    !/your-|tu-|example|xxx/i.test(raw)
  ) {
    return raw;
  }
  return BRAND_MUX_DEFAULT_PLAYBACK_ID_HARDCODED;
}

export const BRAND_MUX_DEFAULT_PLAYBACK_ID = resolveBrandMuxDefaultPlaybackId();

/**
 * Fondo full-screen estático en `/vip/:nombre` (Cloudinary portrait).
 * Prioridad sobre Mux: la página VIP debe cargar rápido y simple.
 * Override: `VITE_VIP_BACKGROUND_IMAGE_URL`.
 */
export const VIP_BACKGROUND_IMAGE_URL = (
  import.meta.env.VITE_VIP_BACKGROUND_IMAGE_URL ??
  "https://res.cloudinary.com/nm-creators/image/upload/v1776912379/background-claryyddzz-mobile-anaafernandez_owj1o8.png"
)
  .toString()
  .trim();

/**
 * Fondo full-screen Mux en `/vip/:nombre` (solo si no hay imagen estática).
 * - Por defecto: `mux-player` (HLS en bucle, silenciado, sin UI).
 * - Con `VITE_VIP_BACKGROUND_MUX_USE_GIF=true`: `image.mux.com/.../animated.gif`.
 */
export const VIP_BACKGROUND_MUX_PLAYBACK_ID = (
  import.meta.env.VITE_VIP_BACKGROUND_MUX_PLAYBACK_ID ?? ""
)
  .toString()
  .trim();

/**
 * Usar el GIF animado de Mux (`/animated.gif`) en lugar del stream HLS.
 * @see https://docs.mux.com/guides/video/get-images-from-a-video (animated GIF)
 */
export const VIP_BACKGROUND_MUX_USE_GIF =
  import.meta.env.VITE_VIP_BACKGROUND_MUX_USE_GIF === "true" ||
  import.meta.env.VITE_VIP_BACKGROUND_MUX_USE_GIF === "1";

/**
 * Query opcional para el GIF (ej. `start=0&end=6&width=960&fps=6`). Sin `?` inicial.
 * Si está vacío se usa `width=1280&fps=8`.
 */
export const VIP_BACKGROUND_MUX_GIF_QUERY = (
  import.meta.env.VITE_VIP_BACKGROUND_MUX_GIF_QUERY ?? ""
)
  .toString()
  .trim();

// ── i18n / SEO ───────────────────────────────────────────────────────────────

/** Nombre del sitio para i18n, Open Graph, StructuredData. */
export const BRAND_SITE_NAME =
  import.meta.env.VITE_BRAND_SITE_NAME ?? BRAND_PERSON_NAME;

// ── Emails (frontend-visible) ─────────────────────────────────────────────────
/** Email de soporte visible al usuario (ej. "hola@anaafernandez.com"). */
export const BRAND_SUPPORT_EMAIL =
  import.meta.env.VITE_BRAND_SUPPORT_EMAIL ??
  import.meta.env.VITE_SUPPORT_EMAIL ??
  `hola@${BRAND_SITE_HOST}`;

// ── URLs de Subdomains ───────────────────────────────────────────────────────

/** URL del admin dashboard/menu (ej. "https://menu.anaafernandez.com"). */
export const BRAND_MENU_URL =
  import.meta.env.VITE_BRAND_MENU_URL ??
  `https://menu.${BRAND_SOURCE_KEY}.com`;

// ── Social Media ─────────────────────────────────────────────────────────────

/** Handle de Instagram sin @ (ej. "anaafernandez"). */
export const BRAND_INSTAGRAM_HANDLE =
  import.meta.env.VITE_BRAND_INSTAGRAM_HANDLE ?? BRAND_SOURCE_KEY;

/** Handle de TikTok sin @ (ej. "anaafernandez"). */
export const BRAND_TIKTOK_HANDLE =
  import.meta.env.VITE_BRAND_TIKTOK_HANDLE ?? BRAND_SOURCE_KEY;

/** Handle de Telegram sin @ (ej. "anaafernandez"). */
export const BRAND_TELEGRAM_HANDLE =
  import.meta.env.VITE_BRAND_TELEGRAM_HANDLE ?? BRAND_SOURCE_KEY;

/** Handle de YouTube sin @ (ej. "anaafernandez"). */
export const BRAND_YOUTUBE_HANDLE =
  import.meta.env.VITE_BRAND_YOUTUBE_HANDLE ?? BRAND_SOURCE_KEY;

/** URL completa de Instagram (ej. "https://www.instagram.com/anaafernandez"). */
export const BRAND_INSTAGRAM_URL =
  import.meta.env.VITE_BRAND_INSTAGRAM_URL ??
  `https://www.instagram.com/${BRAND_INSTAGRAM_HANDLE}`;

/** URL completa de TikTok (ej. "https://www.tiktok.com/@anaafernandez"). */
export const BRAND_TIKTOK_URL =
  import.meta.env.VITE_BRAND_TIKTOK_URL ??
  `https://www.tiktok.com/@${BRAND_TIKTOK_HANDLE}`;

/** URL completa de Telegram (ej. "https://t.me/anaafernandez"). */
export const BRAND_TELEGRAM_URL =
  import.meta.env.VITE_BRAND_TELEGRAM_URL ??
  `https://t.me/${BRAND_TELEGRAM_HANDLE}`;

/** URL completa de YouTube (ej. "https://www.youtube.com/@anaafernandez"). */
export const BRAND_YOUTUBE_URL =
  import.meta.env.VITE_BRAND_YOUTUBE_URL ??
  `https://www.youtube.com/@${BRAND_YOUTUBE_HANDLE}`;

// ── CDN / Assets ─────────────────────────────────────────────────────────────

/** Folder de Cloudinary para portfolio (ej. "anaafernandez/portfolio"). */
export const BRAND_CLOUDINARY_FOLDER =
  import.meta.env.VITE_BRAND_CLOUDINARY_FOLDER ??
  import.meta.env.VITE_CLOUDINARY_BASE_FOLDER ??
  `${BRAND_SOURCE_KEY}/portfolio`;

/**
 * Avatar / foto de perfil del brand (circular en headers, /sorprendeme, bio).
 * Override: `VITE_BRAND_PROFILE_IMAGE_URL`.
 *
 * Default = retrato Cloudinary de Ana Fernández (no usar IDs genéricos de
 * Cloudflare Images compartidos entre brands — rompían el avatar en forks).
 */
/** Crop square del header oficial (Cloudinary `header-anaa-fernandez_qlgnda`). */
const BRAND_PROFILE_IMAGE_URL_DEFAULT =
  "https://res.cloudinary.com/nm-creators/image/upload/w_400,h_400,c_fill,g_auto,q_auto,f_auto/v1776832279/header-anaa-fernandez_qlgnda.png";

export const BRAND_PROFILE_IMAGE_URL = (
  (import.meta.env.VITE_BRAND_PROFILE_IMAGE_URL as string | undefined)?.trim() ||
  BRAND_PROFILE_IMAGE_URL_DEFAULT
).toString();

/**
 * Banner / header landscape del brand.
 * Override: `VITE_BRAND_BANNER_IMAGE_URL` o alias `VITE_BANNER_IMAGE_URL`.
 * Default = header Cloudinary de Ana Fernández (site-settings banner).
 */
const BRAND_BANNER_IMAGE_URL_DEFAULT =
  "https://res.cloudinary.com/nm-creators/image/upload/v1776832279/header-anaa-fernandez_qlgnda.png";

export const BRAND_BANNER_IMAGE_URL = (
  (import.meta.env.VITE_BRAND_BANNER_IMAGE_URL as string | undefined)?.trim() ||
  (import.meta.env.VITE_BANNER_IMAGE_URL as string | undefined)?.trim() ||
  BRAND_BANNER_IMAGE_URL_DEFAULT
).toString();

/**
 * Fondos full-bleed de `/sorprendeme` cuando `VITE_SORPRENDEME_BACKGROUNDS`
 * no está seteado. Lista comma-separated en env, o defaults del brand:
 * VIP mobile + banner + perfil.
 *
 * Override: `VITE_BRAND_SORPRENDEME_BACKGROUNDS` (comma-separated URLs).
 */
function parseCommaUrls(raw: string | undefined): string[] {
  if (!raw || typeof raw !== "string") return [];
  return raw
    .split(",")
    .map((u) => u.trim())
    .filter((u) => u.startsWith("http://") || u.startsWith("https://"));
}

const BRAND_SORPRENDEME_BACKGROUNDS_DEFAULT: string[] = [
  // Mobile portrait (misma que /vip)
  "https://res.cloudinary.com/nm-creators/image/upload/v1776912379/background-claryyddzz-mobile-anaafernandez_owj1o8.png",
  // Header landscape
  BRAND_BANNER_IMAGE_URL_DEFAULT,
  // Gallery portraits (Ana)
  "https://res.cloudinary.com/nm-creators/image/upload/v1776773772/658744452_18100911626298137_5653833037303401866_n_pgbrsh.jpg",
  "https://res.cloudinary.com/nm-creators/image/upload/v1776773771/656294735_18100911617298137_2914584877962209387_n_c3dzb4.jpg",
  "https://res.cloudinary.com/nm-creators/image/upload/v1776773771/656274405_18100820978298137_802901512001914154_n_j03rpf.jpg",
];

export const BRAND_SORPRENDEME_BACKGROUND_URLS: string[] = (() => {
  const fromBrandEnv = parseCommaUrls(
    import.meta.env.VITE_BRAND_SORPRENDEME_BACKGROUNDS as string | undefined,
  );
  if (fromBrandEnv.length > 0) return fromBrandEnv;
  // Prefer VIP static bg as first entry if still the brand default
  const vip = VIP_BACKGROUND_IMAGE_URL?.trim();
  const base = [...BRAND_SORPRENDEME_BACKGROUNDS_DEFAULT];
  if (vip && !base.includes(vip)) {
    return [vip, ...base];
  }
  return base;
})();

// ── Storage Keys (Frontend) ──────────────────────────────────────────────────

/** Prefijo para localStorage/sessionStorage keys (ej. "anaafernandez"). */
export const BRAND_STORAGE_PREFIX = BRAND_SOURCE_KEY;

// ── SEO & Metadata ───────────────────────────────────────────────────────────

/** Descripción del brand para meta tags y SEO. */
export const BRAND_DESCRIPTION =
  import.meta.env.VITE_BRAND_DESCRIPTION ??
  "Modelo de Buenos Aires, 18 años. Apasionada por las redes sociales y el crecimiento.";

/** Locale principal (ej. "es_AR"). */
export const BRAND_LOCALE = import.meta.env.VITE_BRAND_LOCALE ?? "es_AR";

/** Código de idioma (ej. "es"). */
export const BRAND_LANGUAGE = import.meta.env.VITE_BRAND_LANGUAGE ?? "es";

/** País del brand (ej. "Argentina"). */
export const BRAND_COUNTRY = import.meta.env.VITE_BRAND_COUNTRY ?? "Argentina";

/**
 * URL de la imagen OG/Twitter.
 * Override: `VITE_BRAND_OG_IMAGE_URL`.
 * Default = banner oficial del brand (Cloudinary), no el avatar legacy de Cloudflare.
 */
export const BRAND_OG_IMAGE_URL =
  (import.meta.env.VITE_BRAND_OG_IMAGE_URL ?? "").trim() ||
  BRAND_BANNER_IMAGE_URL ||
  `${BRAND_SITE_URL}/ms-icon-310x310.png`;

// ── Meta Pixel (Facebook) ────────────────────────────────────────────────────

/**
 * Meta Pixel ID. Default: `1677147900219941` (Ana Fernández).
 * Override per brand con `VITE_META_PIXEL_ID`.
 * Empty string = pixel desactivado (no se carga el script).
 */
export const BRAND_META_PIXEL_ID = (
  import.meta.env.VITE_META_PIXEL_ID ?? "1677147900219941"
).toString().trim();

/** Código de verificación de Google Search Console. */
export const BRAND_GSC_VERIFICATION =
  import.meta.env.VITE_BRAND_GSC_VERIFICATION ?? "";

// ── Metricool (web analytics) ───────────────────────────────────────────────

/**
 * Hash de tracking Metricool (Connections → Connect website → JavaScript).
 * Override por brand: `VITE_METRICOOL_HASH`. Vacío = desactivado.
 */
export const BRAND_METRICOOL_HASH = (
  import.meta.env.VITE_METRICOOL_HASH ?? "1dd03be4ed504c3f150efc50bd2515a7"
).toString().trim();
